import { m } from '@/paraglide/messages.js';
import { locales, overwriteGetLocale } from '@/paraglide/runtime.js';

export { m };

/**
 * Languages the app offers, with their own names. To add one: add
 * messages/<code>.json, add it to project.inlang/settings.json, run
 * `npm run i18n`, and add a line here (plus the server's src/i18n.ts).
 */
export const LANGUAGES: { code: Locale; name: string; intl: string }[] = [
  { code: 'en', name: 'English', intl: 'en-GB' },
  { code: 'nl', name: 'Nederlands', intl: 'nl-NL' },
];
export type Locale = (typeof locales)[number];

const STORAGE_KEY = 'cr_language';

function read(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** The device's preferred supported language, falling back to English. */
export function deviceLocale(): Locale {
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0];
    const hit = LANGUAGES.find((l) => l.code === base);
    if (hit) return hit.code;
  }
  return 'en';
}

/** A language picked by hand in Settings, if any. Only a manual choice is stored. */
export function pinnedLocale(): Locale | null {
  const v = read(STORAGE_KEY);
  return LANGUAGES.some((l) => l.code === v) ? (v as Locale) : null;
}

export const locale: Locale = pinnedLocale() ?? deviceLocale();
overwriteGetLocale(() => locale);
document.documentElement.lang = locale;
export const intlLocale = LANGUAGES.find((l) => l.code === locale)!.intl;

/** Switch language ('auto' follows the device again). Reloads so every text updates. */
export function chooseLanguage(value: Locale | 'auto') {
  try {
    if (value === 'auto') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  location.reload();
}

type MessageFn = (inputs?: Record<string, unknown>) => string;
/** Translate a message by key (for keys built from data, like badge ids). */
export function msg(key: string, params: Record<string, unknown> = {}): string {
  const fn = (m as unknown as Record<string, MessageFn | undefined>)[key];
  return fn ? fn(params) : key;
}

export const choreName = (c: { name: string | null; key: string | null } | null | undefined) =>
  c ? (c.name ?? (c.key ? msg(`chore_${c.key}`) : '')) : '';

/** Rooms offered when adding a chore; other rooms show up once a chore uses them. */
export const BASE_ROOMS = ['kitchen', 'bathroom', 'bedroom', 'living_room', 'whole_house', 'outdoors'];
/** Every room key the server uses (see ROOMS in starter-chores.ts); anything else is a custom room name. */
export const ROOM_KEYS = [
  ...BASE_ROOMS,
  ...['bathroom_2', 'bathroom_3', 'toilet', 'dining_room', 'office', 'hallway', 'laundry_room', 'guest_room', 'pets', 'garage'],
];
export const roomName = (room: string) => (ROOM_KEYS.includes(room) ? msg(`room_${room}`) : room);

export const levelTitle = (lvl: { titleIndex: number }) => msg(`level_title_${lvl.titleIndex}`);

export const monthName = (month: number) => new Intl.DateTimeFormat(intlLocale, { month: 'short' }).format(new Date(2026, month - 1, 1));

/** Monday-first short weekday names. */
export const weekdayNames = (style: 'narrow' | 'short' | 'long' = 'short') =>
  Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(intlLocale, { weekday: style }).format(new Date(2026, 8, 28 + i)));

export const formatNumber = (n: number, digits = 1) => n.toLocaleString(intlLocale, { maximumFractionDigits: digits });

export function formatDate(date: string | number, opts: Intl.DateTimeFormatOptions) {
  const d = typeof date === 'string' ? new Date(`${date}T12:00:00`) : new Date(date);
  return new Intl.DateTimeFormat(intlLocale, opts).format(d);
}

export function ago(ts: number) {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return m.ago_now();
  if (s < 3600) return m.ago_minutes({ count: Math.floor(s / 60) });
  if (s < 86400) return m.ago_hours({ count: Math.floor(s / 3600) });
  return m.ago_days({ count: Math.floor(s / 86400) });
}
